import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
} from "react-router-dom";
import NotFound from "../pages/404/index";
import Protected from "./protected";
import Page from "../pages/page/index";
import Login from "../pages/user/login/index";
import MFASetup from "../pages/user/mfa-setup/index";
import MFAVerify from "../pages/user/mfa-verify/index";
import UserLogout from "../pages/user/logout/index";
import ForgotPassword from "../pages/user/forgot-password/index";
import SetPassword from "../pages/user/set-password/index";
import UserConfirmation from "../pages/user/registration-confirmation/index";
import ResetPassword from "../pages/user/reset-password";
import AppLayout from "./AppLayout";
import { useGlobalContext } from "../contexts/GlobalContext";
import { appRoutes } from "./appModulesConfig";
import PublicRoutes from "./publicRoutes";
import MFARoutes from "./mfaRoutes";
import UserRoles from "../pages/settings/user-roles";
import { RBAC_RESOURCE_USER_ROLES } from "../contstants";

const generateModuleRoutes = (enabledModules = []) =>
  appRoutes
    .filter((route) => enabledModules.includes(route.module))
    .map(({ path, element, permission, module, allowedRoles }) => (
      <Route
        key={module}
        element={
          <Protected
            requiredPermissions={permission}
            allowedRoles={allowedRoles ?? null}
            module={module}
          />
        }
      >
        <Route path={path} element={element} />
      </Route>
    ));

// Exported function that builds the router using the current context
export const useAppRouter = () => {
  const { allowedModules } = useGlobalContext();
  return createBrowserRouter(
    createRoutesFromElements(
      <Route path="/" element={<AppLayout />}>
        <Route index element={<Navigate to="card-issuing" />} />

        <Route
          element={
            <Protected
              allowedRoles={[
                "owner",
                "admin",
                "client_admin",
                "office_admin",
                "agent",
              ]}
            />
          }
        >
          <Route path="user/logout" element={<UserLogout />} />
          <Route path="settings" element={<Navigate to="/settings/users" />} />
        </Route>

        {/* Dynamic protected routes */}
        {generateModuleRoutes(allowedModules)}

        {/* Roles & Permissions management: dynamic users gated on the
            permission, legacy owner/admin by role. */}
        <Route
          element={
            <Protected
              allowedRoles={["owner", "admin"]}
              requiredDynamicPermission={{
                resource: RBAC_RESOURCE_USER_ROLES,
                access: "read",
              }}
            />
          }
        >
          <Route path="settings/user-roles" element={<UserRoles />} />
        </Route>

        <Route element={<MFARoutes />} >
          <Route path="auth/mfa-setup" element={<MFASetup />} />
          <Route path="auth/mfa-verify" element={<MFAVerify />} />
        </Route>

        <Route path="page/:slug/*" element={<Page />} />
        <Route element={<PublicRoutes />}>
          <Route path="user/login" element={<Login />} />
          <Route path="user/forgot-password" element={<ForgotPassword />} />
          <Route path="auth/register" element={<SetPassword />} />
          <Route path="auth/reset-password" element={<ResetPassword />} />
          <Route
            path="user/registration-confirmation"
            element={<UserConfirmation />}
          />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Route>
    )
  );
};
